if (window.MainCollectionFilterManager && window.initializeMainCollectionFilters) {
  window.initializeMainCollectionFilters(document);
} else {
  class MainCollectionFilterManager {
    constructor(sectionId) {
      this.sectionId = sectionId;
      this.activeValue = null;
      this.inputHandler = this.handleInputChange.bind(this);

      this.collectDom();
      this.attachEvents();
      this.restoreFromUrl();
      this.syncInputStates();
      this.applyFilter();
    }

    collectDom() {
      this.rootElements = Array.from(
        document.querySelectorAll(`[data-custom-filter-root][data-section-id="${this.sectionId}"]`)
      );

      this.inputs = Array.from(
        document.querySelectorAll(`[data-custom-filter-input][data-section-id="${this.sectionId}"]`)
      );

      this.productList = document.querySelector(`#product-list-${this.sectionId}`);
      this.productWrappers = this.productList
        ? Array.from(this.productList.querySelectorAll('[data-product-wrapper]'))
        : [];

      this.countTargets = Array.from(
        document.querySelectorAll(`[data-product-count-target][data-section-id="${this.sectionId}"]`)
      );

      this.emptyState = document.querySelector(`[data-filter-empty][data-section-id="${this.sectionId}"]`);
      this.activeFacetsContainer = document.querySelector(
        `[data-custom-active-facets][data-section-id="${this.sectionId}"]`
      );
    }

    attachEvents() {
      this.detachEvents();

      if (this.inputs.length === 0) {
        return;
      }

      this.inputs.forEach((input) => {
        input.addEventListener('change', this.inputHandler);
      });
    }

    detachEvents() {
      if (!this.inputs) {
        return;
      }

      this.inputs.forEach((input) => {
        input.removeEventListener('change', this.inputHandler);
      });
    }

    refresh() {
      const previousValue = this.activeValue;
      this.collectDom();
      this.attachEvents();
      const normalizedPrevious = this.normalizeValue(previousValue);
      if (normalizedPrevious) {
        const matchingInput = this.inputs.find(
          (input) => this.normalizeValue(input.dataset.filterValue) === normalizedPrevious
        );
        this.activeValue = matchingInput ? normalizedPrevious : null;
      } else {
        this.activeValue = null;
      }
      this.syncInputStates();
      this.applyFilter();
    }

    destroy() {
      this.detachEvents();
      this.activeValue = null;
      this.inputs = [];
      this.productWrappers = [];
      this.countTargets = [];
      this.activeFacetsContainer = null;
      this.rootElements = [];
      this.productList = null;
      this.emptyState = null;
    }

    handleInputChange(event) {
      const input = event.currentTarget;
      const value = input.dataset.filterValue;

      if (!value) {
        return;
      }

      const normalizedValue = this.normalizeValue(value);
      this.activeValue = input.checked && normalizedValue ? normalizedValue : null;

      this.syncInputStates();
      this.applyFilter();
      this.saveFilterToUrl();
    }

    syncInputStates() {
      if (!this.inputs) {
        return;
      }

      this.inputs.forEach((input) => {
        const normalizedValue = this.normalizeValue(input.dataset.filterValue);
        const shouldCheck = this.activeValue === normalizedValue;
        input.checked = shouldCheck;
        if (shouldCheck) {
          input.setAttribute('checked', 'checked');
        } else {
          input.removeAttribute('checked');
        }
      });
    }

    applyFilter() {
      const filterValue = this.activeValue;
      let visibleCount = 0;

      const wrappers = Array.isArray(this.productWrappers) ? this.productWrappers : [];

      wrappers.forEach((wrapper) => {
        const collections = this.getWrapperCollections(wrapper);
        const matches = !filterValue || collections.includes(filterValue);

        wrapper.style.display = matches ? '' : 'none';

        if (matches) {
          visibleCount += 1;
        }
      });

      this.updateCounts(visibleCount, this.activeValue !== null);
      this.toggleEmptyState(visibleCount);
      this.updateActiveFacetDisplay();

      if (this.productList && window.initializePctCardInfoHeights) {
        window.initializePctCardInfoHeights(this.productList);
      }
    }

    getWrapperCollections(wrapper) {
      if (wrapper.__collectionHandles) {
        return wrapper.__collectionHandles;
      }

      const data = wrapper.dataset.productCollections || '[]';
      let handles = [];

      try {
        handles = JSON.parse(data);
      } catch (error) {
        handles = data
          .split(',')
          .map((entry) => entry.trim())
          .filter(Boolean);
      }

      if (!Array.isArray(handles)) {
        handles = [];
      }

      wrapper.__collectionHandles = handles.map((handle) => handle.toString().toLowerCase());
      return wrapper.__collectionHandles;
    }

    updateCounts(visibleCount, filterActive) {
      if (!this.countTargets) {
        return;
      }

      this.countTargets.forEach((target) => {
        if (!filterActive && target.dataset.countOriginal) {
          target.textContent = target.dataset.countOriginal;
          return;
        }

        if (target.dataset.countTemplate) {
          const template = target.dataset.countTemplate;
          const updated = template.split('[count]').join(`${visibleCount}`);
          target.textContent = updated;
          return;
        }

        target.textContent = `${visibleCount}`;
      });
    }

    toggleEmptyState(visibleCount) {
      if (!this.emptyState) {
        return;
      }

      if (visibleCount === 0) {
        this.emptyState.hidden = false;
      } else {
        this.emptyState.hidden = true;
      }
    }

    normalizeValue(value) {
      if (typeof value !== 'string') {
        return null;
      }

      const normalized = value.trim().toLowerCase();
      return normalized === '' ? null : normalized;
    }

    getActiveFacetsContainer() {
      if (this.activeFacetsContainer && document.contains(this.activeFacetsContainer)) {
        return this.activeFacetsContainer;
      }

      this.activeFacetsContainer = document.querySelector(
        `[data-custom-active-facets][data-section-id="${this.sectionId}"]`
      );

      return this.activeFacetsContainer;
    }

    getActiveInput() {
      if (!this.inputs || this.inputs.length === 0) {
        return null;
      }

      return this.inputs.find((input) => input.checked) || null;
    }

    hasDefaultFacetElements(container) {
      if (!container) {
        return false;
      }

      return Array.from(container.querySelectorAll('.removable-facet')).some(
        (facet) => !facet.hasAttribute('data-custom-active-facet')
      );
    }

    buildCustomActiveFacetElement(heading, label) {
      const trimmedLabel = (label || '').trim();
      const trimmedHeading = (heading || '').trim();
      const displayText = trimmedLabel || trimmedHeading || '';
      const accessibleLabel = displayText || 'Filter';

      const facet = document.createElement('div');
      facet.className = 'removable-facet';
      facet.dataset.customActiveFacet = 'true';

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'tap-area link-faded';
      button.setAttribute('aria-label', `Remove filter "${accessibleLabel}"`);
      button.innerHTML =
        '<svg aria-hidden="true" focusable="false" fill="none" width="10" class="icon icon-close" viewBox="0 0 16 16"><path d="m1 1 14 14M1 15 15 1" stroke="currentColor" stroke-width="1.5"></path></svg>';
      button.addEventListener('click', (event) => {
        event.preventDefault();
        this.clearActiveFilter();
      });

      const text = document.createElement('span');
      text.className = 'text-subdued';
      text.textContent = displayText || accessibleLabel;

      facet.append(button, text);
      return facet;
    }

    updateActiveFacetDisplay() {
      const container = this.getActiveFacetsContainer();
      if (!container) {
        return;
      }

      const existingFacet = container.querySelector('[data-custom-active-facet]');
      const activeInput = this.getActiveInput();
      const hasDefaultFacets = this.hasDefaultFacetElements(container);
      container.dataset.hasDefaultActive = hasDefaultFacets ? 'true' : 'false';

      if (activeInput) {
        const label =
          activeInput.dataset.filterLabel ||
          activeInput.nextElementSibling?.textContent?.trim() ||
          activeInput.value ||
          '';
        const heading = activeInput.dataset.filterHeading || '';
        const facetElement = this.buildCustomActiveFacetElement(heading, label);

        if (existingFacet) {
          existingFacet.replaceWith(facetElement);
        } else {
          container.appendChild(facetElement);
        }

        container.hidden = false;
        container.dataset.customActive = 'true';
      } else {
        if (existingFacet) {
          existingFacet.remove();
        }

        container.dataset.customActive = 'false';

        if (!hasDefaultFacets) {
          container.hidden = true;
        }
      }
    }

    clearActiveFilter() {
      this.activeValue = null;
      this.syncInputStates();
      this.applyFilter();
      this.saveFilterToUrl();
    }

    saveFilterToUrl() {
      try {
        const url = new URL(window.location.href);
        if (this.activeValue) {
          url.searchParams.set('_cf', this.activeValue);
        } else {
          url.searchParams.delete('_cf');
        }
        history.replaceState(history.state, '', url.toString());
      } catch (e) {}
    }

    restoreFromUrl() {
      try {
        const savedValue = new URL(window.location.href).searchParams.get('_cf');
        if (savedValue) {
          this.activeValue = savedValue;
        }
      } catch (e) {}
    }
  }

  const mainCollectionFilterInstances = new Map();

  function initializeMainCollectionFilters(root = document) {
    const filterRoots = root.querySelectorAll
      ? root.querySelectorAll('[data-custom-filter-root]')
      : [];

    if (filterRoots.length === 0) {
      return;
    }

    filterRoots.forEach((element) => {
      const sectionId = element.dataset.sectionId;
      if (!sectionId) {
        return;
      }

      const existingInstance = mainCollectionFilterInstances.get(sectionId);

      if (existingInstance) {
        existingInstance.refresh();
        return;
      }

      const instance = new MainCollectionFilterManager(sectionId);
      mainCollectionFilterInstances.set(sectionId, instance);
    });

    // Clean up instances for sections that no longer exist
    mainCollectionFilterInstances.forEach((instance, sectionId) => {
      if (!document.querySelector(`[data-custom-filter-root][data-section-id="${sectionId}"]`)) {
        instance.destroy();
        mainCollectionFilterInstances.delete(sectionId);
      }
    });
  }

  function scheduleInitialize(target = document) {
    requestAnimationFrame(() => {
      initializeMainCollectionFilters(target);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeMainCollectionFilters(document));
  } else {
    initializeMainCollectionFilters(document);
  }

  document.addEventListener(
    'shopify:section:load',
    (event) => {
      scheduleInitialize(event.target || document);
    },
    { passive: true }
  );

  document.addEventListener(
    'facets:updated',
    () => {
      scheduleInitialize(document);
    },
    { passive: true }
  );

  document.documentElement.addEventListener(
    'theme:loading:end',
    () => {
      scheduleInitialize(document);
    },
    { passive: true }
  );

  window.MainCollectionFilterManager = MainCollectionFilterManager;
  window.initializeMainCollectionFilters = initializeMainCollectionFilters;
}
